import type { OpenTab } from "@/components/features/document-tabs"
import { BRAND } from "@/lib/config/brand"

export interface WindowState {
  windowId: string
  documentId: string
  tabState?: OpenTab[]
  activeTabId?: string
}

/**
 * Open a document in a new browser window
 * @param documentId - The document ID to open
 * @param mode - 'move' closes tab in original window, 'copy' keeps it
 * @param currentTabs - Current tabs in the original window
 * @param activeTabId - Currently active tab ID
 * @returns The new window object, or null if popup was blocked
 */
export function openDocumentInNewWindow(
  documentId: string,
  mode: 'move' | 'copy',
  currentTabs: OpenTab[],
  activeTabId: string
): Window | null {
  const windowId = `window-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  
  // Find the tab to open
  const tabToOpen = currentTabs.find(tab => tab.id === documentId)
  if (!tabToOpen) {
    console.error('[WindowManager] Tab not found:', documentId)
    return null
  }
  
  // Build URL with state
  const url = new URL(window.location.href)
  url.searchParams.set('documentId', documentId)
  url.searchParams.set('windowId', windowId)
  url.searchParams.set('mode', mode)
  
  // Store initial state in sessionStorage (keyed by windowId)
  const windowState: WindowState = {
    windowId,
    documentId,
    tabState: mode === 'copy' 
      ? currentTabs // Copy all tabs
      : [tabToOpen], // Move: only the selected tab
    activeTabId: mode === 'copy' ? activeTabId : documentId
  }
  
  try {
    sessionStorage.setItem(`window-state-${windowId}`, JSON.stringify(windowState))
  } catch (err) {
    console.error('[WindowManager] Failed to store window state:', err)
  }
  
  // Open new window
  const newWindow = window.open(
    url.toString(),
    `flownotes-${windowId}`,
    'width=1200,height=800,menubar=no,toolbar=no,location=no'
  )
  
  if (!newWindow) {
    console.error('[WindowManager] Popup blocked or failed to open')
    return null
  }
  
  // Update window title when it loads
  newWindow.addEventListener('load', () => {
    if (tabToOpen.title) {
      newWindow.document.title = `${tabToOpen.title} - ${BRAND.displayName}`
    }
  })
  
  // If move mode, notify original window to close the tab
  if (mode === 'move' && window.opener === null) {
    // We're in the original window, send message to new window when it's ready
    const checkWindowReady = setInterval(() => {
      if (newWindow.closed) {
        clearInterval(checkWindowReady)
        return
      }
      
      try {
        // Try to post message to new window
        newWindow.postMessage({
          type: 'WINDOW_READY',
          windowId,
          tabId: documentId
        }, window.location.origin)
        clearInterval(checkWindowReady)
      } catch (err) {
        // Window might not be ready yet, keep trying
      }
    }, 100)
    
    // Stop trying after 5 seconds
    setTimeout(() => clearInterval(checkWindowReady), 5000)
  }
  
  return newWindow
}

/**
 * Get window state from URL parameters and sessionStorage
 * @returns WindowState if this is a new window, null otherwise
 */
export function getWindowState(): WindowState | null {
  const urlParams = new URLSearchParams(window.location.search)
  const windowId = urlParams.get('windowId')
  
  if (!windowId) return null
  
  try {
    const stateStr = sessionStorage.getItem(`window-state-${windowId}`)
    if (!stateStr) return null
    
    return JSON.parse(stateStr) as WindowState
  } catch (err) {
    console.error('[WindowManager] Failed to parse window state:', err)
    return null
  }
}

/**
 * Clean up window state from sessionStorage
 * @param windowId - The window ID to clean up
 */
export function cleanupWindowState(windowId: string): void {
  try {
    sessionStorage.removeItem(`window-state-${windowId}`)
  } catch (err) {
    console.error('[WindowManager] Failed to cleanup window state:', err)
  }
}

